import React, { useState, useEffect, useRef } from 'react'
import { ChatWindow } from '@/components/chat/chat-window'
import { ChatInput } from '@/components/chat/chat-input'
import { useChatStore } from '@/store/chat'
import { useAuthStore } from '@/store/auth'
import { useParams, useNavigate } from 'react-router-dom'
import { ConversationProps } from '@/entities/conversation'
import { generateUUID, isUUID } from '@/utils/uuids-util'
import { message_status, MessageProps } from '@/entities/message'
import { PageHeader } from '@/components/common/page-header'
import { uploadFile } from '@/utils/upload-util'
import { UserProps } from '@/entities/user'
import { useMediaQuery } from '@/hooks/use-media-query'
import { useViewportOffset } from '@/hooks/use-viewport-offset'
import { cn } from '@/lib/utils'
import { useLoader } from '@/contexts/LoaderContext'
import { useToast } from '@/hooks/use-toast'
import { HTTP_STATUS_CODE } from '@/constants/http'
import { api } from '@/lib/axios'
import { useChatStream } from '@/hooks/use-chat-stream'

const STREAMING_ENABLED = import.meta.env.VITE_CHAT_STREAMING_ENABLED === 'true'

const loc = '@/pages/Chat'

export default function Chat() {
  const { ref } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { toast } = useToast()
  const { showLoader, hideLoader } = useLoader()
  const isMdAndDown = useMediaQuery('(max-width:1123px)')
  const viewportOffset = useViewportOffset()
  const { inited, activeConversation, createNewConversation, sendMessage, deleteMessage, getConversationByRef, setActiveConversation, stopProcessing } = useChatStore()
  const [currentConversation, setCurrentConversation] = useState<ConversationProps>()
  const [isLoadingMessage, setIsLoadingMessage] = useState<boolean>(false)
  const [hasAddedFiles, setHasAddedFiles] = useState<boolean>(false)
  const [failedContent, setFailedContent] = useState<string | null>(null)
  const pendingNinaRefRef = useRef<string | null>(null)

  const { streamingMsgRef, streamingDelta, status: streamStatus, requestStop } = useChatStream({
    userRef: user?.ref ?? null,
    callbacks: {
      onChunkStart: (msgRef) => {
        setFailedContent(null)
        setCurrentConversation((prev) => {
          if (!prev) return prev
          const exists = prev.messages?.some((m) => m.ref === msgRef)
          if (exists) return prev
          if (pendingNinaRefRef.current) {
            const tempRef = pendingNinaRefRef.current
            pendingNinaRefRef.current = null
            return {
              ...prev,
              messages: prev.messages?.map((m) =>
                m.ref === tempRef ? { ...m, ref: msgRef } : m
              ),
            }
          }
          const ninaPlaceholder: MessageProps = {
            ref: msgRef,
            senderId: null,
            content: '',
            status: message_status.PROCESSING,
            createdAt: new Date(),
            paths: [],
            isRead: false,
          }
          return { ...prev, messages: [...(prev.messages ?? []), ninaPlaceholder] }
        })
      },
      onComplete: (payload) => {
        setCurrentConversation((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            messages: prev.messages?.map((m) =>
              m.ref === payload.msgRef
                ? { ...m, content: payload.finalContent, status: message_status.ACTIVE, spentValue: payload.spentValue }
                : m
            ),
          }
        })
      },
      onFail: (payload) => {
        setCurrentConversation((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            // message_status doesn't have FAILED yet — keep the message but
            // remove it from the streaming PROCESSING state by marking DELETED.
            // This hides the placeholder from the message list (filtered out).
            messages: prev.messages?.map((m) =>
              m.ref === payload.msgRef ? { ...m, status: message_status.DELETED } : m
            ),
          }
        })
        toast({ variant: 'destructive', title: 'Erro', description: payload.error })
      },
      onStopped: (payload) => {
        setCurrentConversation((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            messages: prev.messages?.map((m) =>
              m.ref === payload.msgRef
                ? { ...m, content: payload.finalContent, status: message_status.ACTIVE }
                : m
            ),
          }
        })
      },
    },
  })

  useEffect(() => {
    showLoader()
    if (!inited) return

    const fetchData = async () => {
      if (ref && isUUID(ref)) {
        const response = await getConversationByRef(ref)
        if (response) {
          setCurrentConversation(response)
          setActiveConversation(response.ref)
        } else if (activeConversation?.ref == ref) {
          setCurrentConversation(activeConversation)
        } else {
          navigate('/chat', { replace: true })
        }
      } else {
        if (window.location.pathname != '/chat') {
          setActiveConversation(undefined)
          setCurrentConversation(undefined)
          navigate('/chat', { replace: true })
        }
      }
    }

    fetchData()
    hideLoader()
  }, [ref, inited])

  useEffect(() => {
    const hasStreamedText = !!streamingDelta && streamingDelta.length > 0
    setIsLoadingMessage(streamStatus === 'streaming' && !hasStreamedText)
  }, [streamStatus, streamingDelta])

  const triggerStreamForContent = (content: string, urls: string[], conversation: ConversationProps) => {
    const tempNinaRef = generateUUID()
    pendingNinaRefRef.current = tempNinaRef
    const pendingNinaMsg: MessageProps = {
      ref: tempNinaRef,
      senderId: null,
      content: '',
      status: message_status.PROCESSING,
      createdAt: new Date(),
      paths: [],
      isRead: false,
    }
    setCurrentConversation((prev) => {
      const base = prev ?? conversation
      return { ...base, messages: [...(base.messages ?? []), pendingNinaMsg] }
    })

    api.post('/', {
      action: 'message-stream',
      userRef: user?.ref,
      content,
      paths: urls,
      conversationRef: conversation.ref,
    }).catch((err) => {
      const status = err?.response?.status
      const isGatewayTimeout = status === 504 || err?.code === 'ECONNABORTED'
      if (isGatewayTimeout) {
        console.info(`[triggerStreamForContent] HTTP timeout (504/abort) — stream segue via Ably`)
        return
      }
      console.error(`[triggerStreamForContent] streaming POST failed`, err)
      toast({
        variant: 'destructive',
        title: 'Ops...',
        description: 'Não foi possível enviar a mensagem.',
      })
      setIsLoadingMessage(false)
    })

    // Dead-stream watchdog: if no chunk arrives within 60s the placeholder
    // stays PROCESSING forever — remove it and surface the retry banner.
    setTimeout(() => {
      if (pendingNinaRefRef.current !== tempNinaRef) return
      // First chunk never arrived — assume the stream died.
      pendingNinaRefRef.current = null
      setCurrentConversation((prev) =>
        prev
          ? { ...prev, messages: prev.messages?.filter((m) => m.ref !== tempNinaRef) }
          : prev
      )
      setFailedContent(content)
      toast({
        variant: 'destructive',
        title: 'Sem resposta',
        description: 'A Nina demorou demais. Toque em "Tentar novamente".',
      })
    }, 60_000)
  }

  const handleRegenerate = async (lastUserContent: string, ninaMsgRef: string) => {
    if (!currentConversation?.ref) return
    deleteMessage(ninaMsgRef, currentConversation.ref)
    const conversationWithoutNina: ConversationProps = {
      ...currentConversation,
      messages: currentConversation.messages?.filter(m => m.ref !== ninaMsgRef),
    }
    setCurrentConversation(conversationWithoutNina)
    triggerStreamForContent(lastUserContent, [], conversationWithoutNina)
  }

  const handleRetry = () => {
    if (!failedContent || !currentConversation) return
    const content = failedContent
    setFailedContent(null)
    triggerStreamForContent(content, [], currentConversation)
  }

  const handleSendMessage = async (content: string, files?: File[]) => {
    setFailedContent(null)
    try {
      let conversation: ConversationProps | undefined = currentConversation

      if (!currentConversation?.ref) {
        conversation = await createNewConversation()
        window.history.pushState({}, '', `/chat/${conversation?.ref}`)
      }

      const urls: string[] = []
      if (files?.length) {
        for (const file of files) {
          const url = await uploadFile(file, user as UserProps, 'chat')
          if (url) urls.push(url)
        }
      }

      if (content?.length && conversation?.ref) {
        if (!STREAMING_ENABLED) setIsLoadingMessage(true)

        if (STREAMING_ENABLED) {
          const optimisticUserMsg: MessageProps = {
            id: 999999,
            ref: generateUUID(),
            content,
            paths: urls,
            senderId: user?.id,
            createdAt: new Date(),
            isRead: false,
            status: message_status.ACTIVE,
          }
          setCurrentConversation((prev) => {
            const base = prev ?? conversation
            return { ...base, messages: [...(base.messages ?? []), optimisticUserMsg] }
          })

          triggerStreamForContent(content, urls, conversation)
        } else {
          const userMessage: MessageProps = {
            id: 999999,
            ref: generateUUID(),
            content,
            paths: urls,
            senderId: user?.id,
            createdAt: new Date(),
            isRead: false,
          }

          setCurrentConversation({
            ...conversation,
            messages: [...(conversation?.messages || []), userMessage],
          })

          const messageResponse: [MessageProps[], { planBalance: number; balance: number }] | void = await sendMessage(content, conversation?.ref, urls)

          const { setUser } = useAuthStore.getState()

          if (messageResponse) {
            const [messages, balanceResponse] = messageResponse

            if (balanceResponse) {
              setUser({ ...user, balance: balanceResponse.balance, planBalance: balanceResponse.planBalance })
            }

            if (messages) {
              setCurrentConversation(prev => ({ ...prev, messages }))
            }
          } else {
            setUser({ ...user, balance: user?.balance, planBalance: user?.planBalance })
          }
        }
      }
    } catch (err) {
      setIsLoadingMessage(false)
      toast({
        variant: 'destructive',
        title: 'Ops...',
        description: 'Não foi possível enviar a mensagem.'
      })
      console.error(`Unhandled error at ${loc}.handleSendMessage. Details: ${err}`)
    } finally {
      if (!STREAMING_ENABLED) {
        setIsLoadingMessage(false)
      }
    }
  }

  const handleStopProcessing = async () => {
    // If a streaming response is active, use the Ably-based stop
    if (streamingMsgRef) {
      await requestStop(streamingMsgRef)
      return
    }

    if (!currentConversation?.ref) {
      toast({
        variant: 'destructive',
        title: 'Ops...',
        description: 'Não foi possível parar o processamento da mensagem.'
      })
      return
    }

    // setIsStoppingProcessingLoading(true)
    const response = await stopProcessing(currentConversation?.ref)

    if (response) {
      setCurrentConversation(response as ConversationProps)
      setIsLoadingMessage(false)
      // setIsStoppingProcessingLoading(false)
    }
  }

  return (
    <>
      <div className={cn(isMdAndDown && 'relative')}>
        <PageHeader title="Consultor IA Mãe Nutrida" description="Tire suas dúvidas sobre casais tentantes, gestantes, crianças e lactantes" showBackButton={!!currentConversation?.ref} />
      </div>

      {currentConversation?.ref && currentConversation?.messages?.length ? (
        <div className="flex flex-col w-full h-full pt-2 rounded-xl items-center justify-between">
          <div className={cn('h-full overflow-y-auto overflow-x-hidden', isMdAndDown ? 'w-full mx-auto px-3 pb-8 max-h-auto' : 'w-full max-w-[1000px] px-[20px] py-10 max-h-auto justify-center items-center')}>
            <div className="pt-6 pb-20 md:pt-12">
              <ChatWindow
                conversation={currentConversation}
                user={user}
                onDeleteMessage={(messageRef, convRef) => {
                  deleteMessage(messageRef, convRef)
                  setCurrentConversation(prev => prev ? {
                    ...prev,
                    messages: prev.messages?.filter(m => m.ref !== messageRef)
                  } : prev)
                }}
                isLoadingMessage={isLoadingMessage}
                hasAddedFiles={hasAddedFiles}
                streamingMsgRef={streamingMsgRef}
                streamingDelta={streamingDelta}
                streamStatus={streamStatus}
                onRegenerate={handleRegenerate}
              />
            </div>
            <div
              className={cn('bg-background fixed rounded-t-3xl transition-[bottom] duration-150 ease-out', isMdAndDown ? 'left-0 right-0 w-[95vw] mx-auto' : 'w-full max-w-[1000px]')}
              style={{ bottom: viewportOffset }}
            >
              {failedContent && (
                <div className="mb-2 flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-destructive/10 text-destructive text-sm">
                  <span>Sua mensagem não foi enviada.</span>
                  <button
                    type="button"
                    onClick={handleRetry}
                    className="font-medium underline-offset-2 hover:underline"
                  >
                    Tentar novamente
                  </button>
                </div>
              )}
              <div className="rounded-3xl w-full h-auto p-2 bg-card border border-border shadow-card">
                <ChatInput onSendMessage={handleSendMessage} disabled={!user} onAddedFiles={setHasAddedFiles} onStopProcessing={handleStopProcessing} isLoadingMessage={isLoadingMessage} noMessages={!!currentConversation?.messages?.length} conversationRef={currentConversation?.ref} streamingMsgRef={streamingMsgRef} />
              </div>
              <div className={cn('bg-transparent text-center w-auto h-auto px-6 pt-2 text-xs leading-relaxed text-muted-foreground/80 mdAndDown:pb-2')}>A Nina é uma ferramenta de apoio e pode conter erros. Não substitui o raciocínio, a experiência ou o julgamento técnico do profissional de saúde.</div>
            </div>
          </div>
        </div>
      ) : (
        <div className={cn('flex flex-col w-full h-full  rounded-xl items-center justify-center', isMdAndDown ? 'px-2 mt-40' : 'px-32 mt-44')}>
          <span className={cn('mb-5 text-foreground font-semibold', isMdAndDown ? 'text-2xl' : 'text-3xl')}>Em que posso ajudar?</span>
          {failedContent && (
            <div className="mb-2 w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-destructive/10 text-destructive text-sm">
              <span>Sua mensagem não foi enviada.</span>
              <button
                type="button"
                onClick={handleRetry}
                className="font-medium underline-offset-2 hover:underline"
              >
                Tentar novamente
              </button>
            </div>
          )}
          <div className="rounded-3xl w-full p-2 bg-card border border-border shadow-card">
            <ChatInput onSendMessage={handleSendMessage} disabled={!user} onAddedFiles={setHasAddedFiles} onStopProcessing={handleStopProcessing} isLoadingMessage={isLoadingMessage} noMessages={!!currentConversation?.messages?.length} conversationRef={currentConversation?.ref} streamingMsgRef={streamingMsgRef} />
          </div>
        </div>
      )}
    </>
  )
}
